import React, { useMemo } from "react";
import DashboardPeriodDropdown from "./DashboardPeriodDropdown";

/**
 * The Month and Year pickers that lead the filter bar on the birthday lists —
 * Birthdays this Month and In-Law Birthdays — ahead of the sorts, parted from
 * them by the bar's red divider. Rendered through `leadingSortButtons`, so on
 * a phone they scroll sideways with the rest of the bar instead of taking a
 * line of their own.
 *
 * Month is 1–12 or DASHBOARD_PERIOD_ALL_MONTHS for the whole year, month by
 * month. Year starts at the current year and runs a few years on — never a
 * past year, since a birthday list is read for what is coming, not what has
 * been. A year restored from elsewhere still shows.
 *
 * @param {object} props
 * @param {number|string} props.month
 * @param {(month: number|string) => void} props.onMonthChange
 * @param {number} props.year
 * @param {(year: number) => void} props.onYearChange
 */

export const DASHBOARD_PERIOD_ALL_MONTHS = "all";

export const DASHBOARD_PERIOD_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** The year picker starts at the current year and runs this many years on. */
const YEAR_PICKER_SPAN = 5;

/** The month picker's own list: January–December, then All Months. */
export const DASHBOARD_PERIOD_MONTH_OPTIONS = [
  ...DASHBOARD_PERIOD_MONTH_NAMES.map((name, index) => ({
    value: index + 1,
    label: name,
  })),
  { value: DASHBOARD_PERIOD_ALL_MONTHS, label: "All Months" },
];

export default function DashboardPeriodPickers({
  month,
  onMonthChange,
  year,
  onYearChange,
}) {
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const years = [];
    for (let y = current; y <= current + YEAR_PICKER_SPAN; y++) {
      years.push(y);
    }
    if (!years.includes(year)) years.push(year);
    return years.sort((a, b) => a - b).map((y) => ({ value: y, label: String(y) }));
  }, [year]);

  return (
    <div className="dashboard-detail-period" role="group" aria-label="Report period">
      <DashboardPeriodDropdown
        label="Month"
        options={DASHBOARD_PERIOD_MONTH_OPTIONS}
        value={month}
        onChange={(next) =>
          onMonthChange(
            next === DASHBOARD_PERIOD_ALL_MONTHS
              ? DASHBOARD_PERIOD_ALL_MONTHS
              : parseInt(next, 10)
          )
        }
      />
      <DashboardPeriodDropdown
        label="Year"
        options={yearOptions}
        value={year}
        onChange={(next) => onYearChange(parseInt(next, 10))}
      />
      <span className="dashboard-detail-sort-divider" aria-hidden="true" />
    </div>
  );
}
